import React, { useState, useEffect } from 'react';
import { fetchNews } from '../services/api';
import { XMarkIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import Navigation from '../components/Navigation';
import SmallHero from '../components/SmallHero';
import '../styles/animations.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { NewsItem } from '../types/conference';

const News: React.FC = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  
  useEffect(() => {
    const loadNews = async () => {
      try {
        setLoading(true);
        const response = await fetchNews();
        
        // Extract news items from the response
        const rawNewsItems = response.news || [];
        
        // Convert backend field names to frontend field names
        const formattedNewsItems: NewsItem[] = rawNewsItems.map((item: any): NewsItem => ({
          id: item.id?.toString?.() ?? String(item.id),
          title: item.title ?? '',
          content: item.content ?? '',
          publishDate: item.published_date ?? new Date().toISOString().split('T')[0],
          author: item.author || 'Conference Team',
          category: item.category || 'announcement',
          featured: Boolean(item.featured),
          status: item.status === 'draft' ? 'draft' : 'published'
        }));
        
        // Get today's date at the start of the day (midnight) for comparison
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Only show published news items with publish dates today or in the past
        const publishedNews = formattedNewsItems.filter((item: NewsItem) => 
          item.status === 'published' && new Date(item.publishDate) <= today
        );
        
        // Separate featured and non-featured news items
        const featuredNews = publishedNews.filter((item: NewsItem) => item.featured);
        const nonFeaturedNews = publishedNews.filter((item: NewsItem) => !item.featured);
        
        // Sort both groups by publish date (newest first)
        featuredNews.sort((a: NewsItem, b: NewsItem) => {
          return new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime();
        });
        
        nonFeaturedNews.sort((a: NewsItem, b: NewsItem) => {
          return new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime();
        });
        
        // Combine: featured news first, then non-featured news
        const sortedNews = [...featuredNews, ...nonFeaturedNews];
        
        setNews(sortedNews);
        setError(null);
      } catch (err) {
        console.error('Error fetching news:', err);
        setError('Could not load news items.');
      } finally {
        setLoading(false);
      }
    };

    loadNews();
  }, []);

  const openNewsModal = (item: NewsItem) => {
    setSelectedNews(item);
    setIsModalOpen(true);
    // Prevent scrolling of the background when modal is open
    document.body.style.overflow = 'hidden';
  };
  
  const closeNewsModal = () => {
    setIsModalOpen(false);
    setSelectedNews(null);
    // Re-enable scrolling
    document.body.style.overflow = 'auto';
  };

  // Get unique categories
  const categories = React.useMemo(() => {
    const allCategories = Array.from(new Set(news.map(item => item.category)));
    return ['all', ...allCategories];
  }, [news]);

  // Filter news by category
  const filteredNews = React.useMemo(() => {
    if (!activeCategory || activeCategory === 'all') {
      return news;
    }
    return news.filter(item => item.category === activeCategory);
  }, [news, activeCategory]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-gray-50">
      <Navigation />
      <SmallHero 
        title="Conference News" 
        subtitle="Stay updated with the latest announcements and information"
      />
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">

        {/* Category Filter */}
        <div className="mb-10 animate-fade-in">
          <div className="flex flex-wrap items-center justify-center gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category === 'all' ? null : category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                  (category === 'all' && !activeCategory) || category === activeCategory
                    ? 'bg-[#00529B] text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#00529B]"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 mb-8">
            <p>{error}</p>
          </div>
        ) : filteredNews.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <p className="text-gray-500 text-lg">No news items available in this category.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-fade-in-delay-1">
            {filteredNews.map((item) => (
              <div 
                key={item.id} 
                className={`bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden ${
                  item.featured ? 'border-l-4 border-[#00529B]' : ''
                }`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <h2 className="text-xl font-bold text-gray-900">{item.title}</h2>
                    {item.featured && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ml-2">
                        Featured
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-500 mb-4">
                    <span>{item.author}</span>
                    <span className="mx-2">•</span>
                    <span>{new Date(item.publishDate).toLocaleDateString()}</span>
                    {item.category && (
                      <>
                        <span className="mx-2">•</span>
                        <span className="capitalize">{item.category}</span>
                      </>
                    )}
                  </div>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {item.content
                      .replace(/<[^>]*>/g, '') // Remove HTML tags
                      .replace(/!\[.*?\]\(.*?\)/g, '') // Remove image syntax (do this before link syntax)
                      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Extract link text, discard URL
                      .replace(/[#*_~`]/g, '') // Remove remaining markdown syntax
                      .replace(/\n+/g, ' ') // Replace line breaks with spaces
                      .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
                      .trim()
                    }
                  </p>
                  
                  <button 
                    onClick={() => openNewsModal(item)}
                    className="inline-flex items-center text-[#00529B] hover:text-[#003D74] font-medium"
                  >
                    Read full article
                    <ChevronRightIcon className="ml-1 h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* News Modal */}
      {isModalOpen && selectedNews && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="news-modal" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={closeNewsModal}></div>
            
            {/* Modal Panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                  onClick={closeNewsModal}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-2xl leading-6 font-bold text-gray-900 mb-4" id="modal-title">
                      {selectedNews.title}
                    </h3>
                    
                    <div className="flex items-center text-sm text-gray-500 mb-6">
                      <span>{selectedNews.author}</span>
                      <span className="mx-2">•</span>
                      <span>{new Date(selectedNews.publishDate).toLocaleDateString()}</span>
                      {selectedNews.category && (
                        <>
                          <span className="mx-2">•</span>
                          <span className="capitalize">{selectedNews.category}</span>
                        </>
                      )}
                      {selectedNews.featured && (
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Featured
                        </span>
                      )}
                    </div>
                    
                    <div className="mt-2 prose prose-blue max-w-none">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {selectedNews.content}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#00529B] sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={closeNewsModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default News;
